import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import { AuthService } from './auth.service';
import {
    ICashIncentiveClaim,
    ICashIncentiveRegister,
    ICreateCashIncentiveClaimRequest,
    ICorrectCashIncentiveRateRequest,
    ICorrectCashIncentiveReceiptRequest,
    IUpdateCashIncentiveClaimRequest,
    IUpdateCashIncentiveClaimStatusRequest,
    IncentiveClaimStatus,
} from '../interfaces/commercial.interface';

const headers = () => AuthService.getAuthHeader();

export const CashIncentiveClaimService = {
    /** R-3: always resolves — a disabled module returns `{ enabled: false, disabledReason }`
     * with an empty page, never a rejected promise. Callers render the banner, not an error. */
    list: async (institutionId: string, page = 0, size = 20, status?: IncentiveClaimStatus | ''): Promise<ICashIncentiveRegister> => {
        const params: Record<string, string | number> = { page, size };
        if (status) params.status = status;
        const res = await axios.get(ENDPOINTS.CASH_INCENTIVE_CLAIM.LIST(institutionId), { headers: headers(), params });
        return res.data;
    },

    get: async (institutionId: string, claimId: string): Promise<ICashIncentiveClaim> =>
        (await axios.get(ENDPOINTS.CASH_INCENTIVE_CLAIM.GET(institutionId, claimId), { headers: headers() })).data,

    create: async (institutionId: string, request: ICreateCashIncentiveClaimRequest): Promise<ICashIncentiveClaim> =>
        (await axios.post(ENDPOINTS.CASH_INCENTIVE_CLAIM.CREATE(institutionId), request, { headers: headers() })).data,

    update: async (institutionId: string, claimId: string, request: IUpdateCashIncentiveClaimRequest): Promise<ICashIncentiveClaim> =>
        (await axios.put(ENDPOINTS.CASH_INCENTIVE_CLAIM.UPDATE(institutionId, claimId), request, { headers: headers() })).data,

    updateStatus: async (institutionId: string, claimId: string, request: IUpdateCashIncentiveClaimStatusRequest): Promise<ICashIncentiveClaim> =>
        (await axios.put(ENDPOINTS.CASH_INCENTIVE_CLAIM.UPDATE_STATUS(institutionId, claimId), request, { headers: headers() })).data,

    correctReceipt: async (institutionId: string, claimId: string, request: ICorrectCashIncentiveReceiptRequest): Promise<ICashIncentiveClaim> =>
        (await axios.put(ENDPOINTS.CASH_INCENTIVE_CLAIM.CORRECT_RECEIPT(institutionId, claimId), request, { headers: headers() })).data,

    correctRate: async (institutionId: string, claimId: string, request: ICorrectCashIncentiveRateRequest): Promise<ICashIncentiveClaim> =>
        (await axios.put(ENDPOINTS.CASH_INCENTIVE_CLAIM.CORRECT_RATE(institutionId, claimId), request, { headers: headers() })).data,

    /** Retry the ledger entry a claim is due (approval when APPROVED, receipt when RECEIVED). */
    postToLedger: async (institutionId: string, claimId: string): Promise<ICashIncentiveClaim> =>
        (await axios.post(ENDPOINTS.CASH_INCENTIVE_CLAIM.POST_LEDGER(institutionId, claimId), {}, { headers: headers() })).data,

    remove: async (institutionId: string, claimId: string): Promise<void> => {
        await axios.delete(ENDPOINTS.CASH_INCENTIVE_CLAIM.DELETE(institutionId, claimId), { headers: headers() });
    },
};
